import { Box, Typography, LinearProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface PasswordRequirement {
  text: string;
  met: boolean;
}

interface PasswordStrengthIndicatorProps {
  password: string;
}

export const PasswordStrengthIndicator = ({
  password,
}: PasswordStrengthIndicatorProps) => {
  const requirements: PasswordRequirement[] = [
    { text: 'At least 8 characters', met: password.length >= 8 },
    { text: 'Contains uppercase letter', met: /[A-Z]/.test(password) },
    { text: 'Contains lowercase letter', met: /[a-z]/.test(password) },
    { text: 'Contains number', met: /\d/.test(password) },
    { text: 'Contains special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  const metCount = requirements.filter((r) => r.met).length;
  const strength = (metCount / requirements.length) * 100;

  const getStrengthColor = () => {
    if (strength < 40) return 'error';
    if (strength < 80) return 'warning';
    return 'success';
  };

  const getStrengthLabel = () => {
    if (strength < 40) return 'Weak';
    if (strength < 80) return 'Medium';
    return 'Strong';
  };

  if (!password) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" sx={{ mr: 2 }}>
          Password Strength: <strong>{getStrengthLabel()}</strong>
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={strength}
            color={getStrengthColor()}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 1 }}>
        {requirements.map((req, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {req.met ? (
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
            ) : (
              <CancelIcon sx={{ fontSize: 16, color: 'error.main', mr: 1 }} />
            )}
            <Typography
              variant="caption"
              sx={{ color: req.met ? 'success.main' : 'text.secondary' }}
            >
              {req.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

